import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode } from "react";

export function Spinner({ className = "" }: { className?: string }) {
  return (
    <span
      className={`inline-block h-4 w-4 rounded-full border-2 border-white/25 border-t-white spin ${className}`}
      aria-label="загрузка"
    />
  );
}

export function PageLoader() {
  return (
    <div className="flex min-h-[60vh] items-center justify-center">
      <Spinner className="h-6 w-6" />
    </div>
  );
}

export function Card({ children, className = "" }: { children: ReactNode; className?: string }) {
  return (
    <div className={`rounded-2xl bg-surface-card border border-white/5 p-4 ${className}`}>
      {children}
    </div>
  );
}

export function Button({
  variant = "primary",
  loading = false,
  children,
  className = "",
  disabled,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: "primary" | "ghost" | "danger";
  loading?: boolean;
}) {
  const base =
    "inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed";
  const styles: Record<string, string> = {
    primary: "bg-brand text-surface-base hover:bg-brand-400",
    ghost: "bg-surface-raised text-white hover:bg-white/10",
    danger: "bg-red-500/15 text-red-300 hover:bg-red-500/25",
  };
  return (
    <button
      className={`${base} ${styles[variant]} ${className}`}
      disabled={disabled || loading}
      {...rest}
    >
      {loading && <Spinner />}
      {children}
    </button>
  );
}

export function Field({
  label,
  hint,
  ...rest
}: InputHTMLAttributes<HTMLInputElement> & { label?: string; hint?: string }) {
  return (
    <label className="block">
      {label && <span className="mb-1 block text-xs text-white/50">{label}</span>}
      <input
        className="w-full rounded-xl bg-surface-raised px-3 py-2.5 text-sm outline-none ring-1 ring-white/5 focus:ring-brand/60 placeholder:text-white/30"
        {...rest}
      />
      {hint && <span className="mt-1 block text-xs text-white/40">{hint}</span>}
    </label>
  );
}

export function Badge({ tone = "muted", children }: { tone?: "green" | "amber" | "red" | "muted"; children: ReactNode }) {
  const styles: Record<string, string> = {
    green: "bg-brand/15 text-brand-400",
    amber: "bg-amber-400/15 text-amber-300",
    red: "bg-red-500/15 text-red-300",
    muted: "bg-white/10 text-white/60",
  };
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${styles[tone]}`}>
      {children}
    </span>
  );
}

export function ErrorText({ children }: { children: ReactNode }) {
  if (!children) return null;
  return <p className="text-sm text-red-300">{children}</p>;
}

export function Stat({ label, value, sub }: { label: string; value: ReactNode; sub?: ReactNode }) {
  return (
    <div className="rounded-xl bg-surface-raised p-3">
      <div className="text-xs text-white/45">{label}</div>
      <div className="mt-1 text-lg font-semibold tabular-nums">{value}</div>
      {sub && <div className="mt-0.5 text-xs text-white/40">{sub}</div>}
    </div>
  );
}

export function Row({ left, right }: { left: ReactNode; right: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3 py-2 border-b border-white/5 last:border-0">
      <div className="min-w-0">{left}</div>
      <div className="shrink-0 text-right">{right}</div>
    </div>
  );
}

// ── форматирование ──
export const usd = (n: number) => `$${(n ?? 0).toLocaleString("ru-RU", { maximumFractionDigits: 2 })}`;
export const rub = (n: number) => `${(n ?? 0).toLocaleString("ru-RU", { maximumFractionDigits: 0 })} ₽`;
export const pct = (n: number) => `${(n ?? 0).toLocaleString("ru-RU", { maximumFractionDigits: 2 })}%`;
export function fmtDate(iso: string | null): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("ru-RU", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return "—";
  }
}
